/**
 * useMapSolids - the map's solids: the terrain's, and every Object that counts as terrain (2026-10-26).
 *
 * The boxes are DERIVED from the roster and the tokens (engine/objects/objectSolids), so they are rebuilt only when
 * one of them would change - a digest of the objects' fields, places and health, not the whole token record, so a
 * figure walking elsewhere keeps the solids' identity and every memo downstream of them (the scene, sight, reach).
 *
 * @param {object|null} terrain
 * @param {object[]} roster - the Combatants
 * @param {Record<string, object>} tokens - by combatant id, as this screen holds them (a player's has no hidden ones)
 * @param {Set<string>} [hiddenIds] - the GM's hidden figures: their boxes are hidden solids
 * @returns {{ solids: object[], tooBig: string[] }} the solids, and the objects too large for this map
 */
import { useMemo, useRef } from 'react';
import { terrainSolids } from '../engine/battlefield/presets';
import { objectDigest, objectSolids, withObjectSolids } from '../engine/objects/objectSolids';

const NONE = { solids: [], tooBig: [] };

export default function useMapSolids(terrain, roster, tokens, hiddenIds) {
  const base = useMemo(() => terrainSolids(terrain), [terrain]);
  const boundary = terrain?.boundary ?? null;
  // The input changes only on a write; the digest (a string) then says whether any box would change, so the solids
  // keep their identity through every write that touches no box (the review: it ran on every render before).
  const input = useMemo(
    () => ({ roster: roster ?? [], tokens: tokens ?? {}, boundary, hiddenIds }),
    [roster, tokens, boundary, hiddenIds],
  );
  const digest = useMemo(() => objectDigest(input), [input]);
  // Read through a ref: keyed on the digest, which is everything the boxes depend on.
  const live = useRef(input);
  live.current = input;
  const objects = useMemo(() => (digest ? objectSolids(live.current) : NONE), [digest]);
  const solids = useMemo(() => withObjectSolids(base, objects), [base, objects]);
  return { solids, tooBig: objects.tooBig };
}
